import { addHours, format, parse } from 'date-fns';
import { DayOfWeek, ITimeZone } from '@lb/api/mylb/interfaces';
import { convertDayBetweenTimezones, convertTimeBetweenTimezones } from '@lb/functions/timezone-utils';

/** Minimal shape of a stored schedule selection needed to render a lesson time slot. */
export interface ILessonScheduleSlot {
	dayIndex: number;
	startTime: string;
	endTime?: string | null;
	timezoneID?: number | null;
}

/** A schedule selection formatted for display in the user-selected timezone. */
export interface ILessonTimeSlot {
	day: string;
	startTime: string;
	displayValue: string;
}

export interface IBuildLessonTimeSlotsOptions {
	schedules: ILessonScheduleSlot[];
	availableTimezones: ITimeZone[];
	selectedTimezone: ITimeZone | null;
	/** Timezone the schedule was originally stored in; used when a slot has no timezoneID of its own. */
	fallbackTimezoneID?: number | null;
}

/**
 * Converts stored schedule selections into display-ready lesson time slots in the
 * user-selected timezone. Shared between the enrollment card and the enrollment
 * overview so both render identical times.
 */
export function buildLessonTimeSlots(options: IBuildLessonTimeSlotsOptions): ILessonTimeSlot[] {
	const { schedules, availableTimezones, selectedTimezone, fallbackTimezoneID } = options;
	if (!schedules?.length) {
		return [];
	}

	const fallbackTimezone = resolveTimezone(fallbackTimezoneID, availableTimezones);

	return schedules.map((schedule) => {
		const sourceTimezone = resolveTimezone(schedule.timezoneID, availableTimezones) ?? fallbackTimezone;
		const targetTimezone = selectedTimezone ?? sourceTimezone;

		if (sourceTimezone && targetTimezone) {
			const normalizedStart = normalizeTime(schedule.startTime) ?? schedule.startTime;
			const normalizedEnd = normalizeTime(schedule.endTime) ?? defaultEndTime(schedule.startTime);
			const convertedStart = convertTimeBetweenTimezones(normalizedStart, sourceTimezone, targetTimezone);
			const convertedEnd = convertTimeBetweenTimezones(normalizedEnd, sourceTimezone, targetTimezone);
			const dayIndex =
				sourceTimezone.timeZoneID === targetTimezone.timeZoneID
					? schedule.dayIndex
					: convertDayBetweenTimezones(schedule.dayIndex, normalizedStart, sourceTimezone, targetTimezone);

			return {
				displayValue: `${formatDisplayTime(convertedStart)} - ${formatDisplayTime(convertedEnd)}`,
				startTime: convertedStart,
				day: resolveDayName(dayIndex, schedule.dayIndex)
			};
		}

		const fallbackStart = parseTimeToDate(schedule.startTime);
		const fallbackEnd = parseTimeToDate(schedule.endTime) ?? (fallbackStart ? addHours(fallbackStart, 1) : null);

		if (fallbackStart && fallbackEnd) {
			return {
				displayValue: `${format(fallbackStart, 'hh:mm a')} - ${format(fallbackEnd, 'hh:mm a')}`,
				startTime: format(fallbackStart, 'HH:mm'),
				day: resolveDayName(schedule.dayIndex, schedule.dayIndex)
			};
		}

		return {
			displayValue: `${schedule.startTime}${schedule.endTime ? ` - ${schedule.endTime}` : ''}`,
			startTime: schedule.startTime,
			day: resolveDayName(schedule.dayIndex, schedule.dayIndex)
		};
	});
}

function resolveTimezone(timezoneID: number | null | undefined, timezones: ITimeZone[]): ITimeZone | null {
	if (timezoneID == null) {
		return null;
	}
	return timezones.find((tz) => tz.timeZoneID === timezoneID) ?? null;
}

function normalizeTime(time: string | null | undefined): string | null {
	if (!time) {
		return null;
	}
	const trimmed = time.trim();
	if (trimmed === '24:00') {
		return '00:00';
	}
	const parts = trimmed.split(':');
	if (parts.length < 2) {
		return null;
	}
	return `${parts[0]?.padStart(2, '0')}:${parts[1]?.padStart(2, '0')}`;
}

function parseTimeToDate(time: string | null | undefined): Date | null {
	const normalized = normalizeTime(time);
	if (!normalized) {
		return null;
	}
	return parse(normalized, 'HH:mm', new Date());
}

function defaultEndTime(startTime: string): string {
	const parsed = parseTimeToDate(startTime);
	if (!parsed) {
		return '00:00';
	}
	return format(addHours(parsed, 1), 'HH:mm');
}

function formatDisplayTime(time: string): string {
	const parsed = parseTimeToDate(time);
	return parsed ? format(parsed, 'hh:mm a') : time;
}

function resolveDayName(preferredDayIndex: number, fallbackDayIndex: number): string {
	return DayOfWeek[preferredDayIndex]?.longName ?? DayOfWeek[fallbackDayIndex]?.longName ?? '';
}
